import {
  Flex,
  IconButton,
  IconEditLine,
  IconTrashLine,
  Pill,
  Text,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import { TermService } from "../../../services/TermService";
import { TermsContext } from "../../../utils/Context";
import { Card } from "../../Card";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../../DialogYesNo/DialogYesNo";
import TermModal from "./TermModal";

const TermCard = ({ term }) => {
  const {
    canvasTerms,
    setCanvasTerms,
    insightsTerms,
    setInsightsTerms,
    setAcademicYears,
  } = useContext(TermsContext);
  const showNotification = useNotification();
  const [deleteModal, setDeleteModal] = useState(false);
  const [termModal, setTermModal] = useState(false);

  const handleUpdateTerm = async (
    termName,
    startDate,
    endDate,
    includedCanvasTerms
  ) => {
    await TermService.updateInsightsTerm(term.id, {
      name: termName,
      start_at: startDate,
      end_at: endDate,
      canvasTerms: includedCanvasTerms,
    })
      .then((updatedTerm) => {
        let newInsightsTerms = [...insightsTerms];
        newInsightsTerms = newInsightsTerms.map((t) => {
          if (t.start_at === "default") {
            const defaultTerms = t.canvas_terms;
            // Add terms back to default term list
            term.canvas_terms.forEach((t) => {
              if (!includedCanvasTerms.includes(t.id) && t.canvas_id !== -1) {
                defaultTerms.push(t);
              }
              if (!includedCanvasTerms.includes(t.id) && t.canvas_id === -1) {
                // Remove default future term from canvasTerms list
                setCanvasTerms((prevCanvasTerms) =>
                  prevCanvasTerms.filter((ct) => ct.id !== t.id)
                );
              }
            });
            // Remove terms from default term list
            includedCanvasTerms.forEach((t) => {
              if (defaultTerms.map((dt) => dt.id).includes(t)) {
                defaultTerms.splice(
                  defaultTerms.findIndex((dt) => dt.id === t),
                  1
                );
              }
            });

            return { ...t, canvas_terms: defaultTerms };
          }
          return t;
        });

        // Add new Canvas term if it is updated with default future term
        updatedTerm.canvas_terms.forEach((t) => {
          if (t.is_placeholder && !canvasTerms.find((ct) => ct.id === t.id)) {
            // Add new future canvas term to canvasTerms list
            setCanvasTerms((prevCanvasTerms) => [...prevCanvasTerms, t]);
          }
        });

        newInsightsTerms = newInsightsTerms.map((t) =>
          t.id === updatedTerm.id ? updatedTerm : t
        );
        setInsightsTerms(newInsightsTerms);
        setTermModal(false);
        showNotification(
          t("notifications.general.updateSuccess", { obj: "Term" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.updateError", { obj: "Term" }),
          ERROR
        )
      );
  };

  const handleDeleteTerm = async () => {
    await TermService.deleteInsightsTerm(term.id)
      .then(() => {
        let newInsightsTerms = [...insightsTerms];
        // add canvas terms back to default term canvas_terms list
        newInsightsTerms = newInsightsTerms.map((t) => {
          if (t.start_at === "default") {
            return {
              ...t,
              canvas_terms: t.canvas_terms.concat(
                term.canvas_terms.filter((ct) => ct.canvas_id !== -1)
              ),
            };
          }
          return t;
        });
        // Remove future term from canvasTerms list
        const futureTerm = term.canvas_terms.find((t) => t.canvas_id === -1);
        if (futureTerm) {
          setCanvasTerms((prevCanvasTerms) =>
            prevCanvasTerms.filter((t) => t.id !== futureTerm.id)
          );
        }
        setInsightsTerms(newInsightsTerms.filter((t) => t.id !== term.id));
        // Remove term from academic year
        setAcademicYears((prevYears) =>
          prevYears.map((year) => {
            return {
              ...year,
              insights_terms: year.insights_terms.filter(
                (t) => t.id !== term.id
              ),
            };
          })
        );
        setDeleteModal(false);
        showNotification(
          t("notifications.general.deleteSuccess", { obj: "Term" })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.deleteError", { obj: "Term" }),
          ERROR
        )
      );
  };

  return (
    <Card
      style={{
        color: "#0D323F",
        backgroundColor: "#F2F8FA",
        border: "1px solid #ccc",
        borderRadius: 10,
        padding: "10px 15px",
      }}
    >
      <Flex gap="small" alignItems="start" wrap="wrap">
        <Flex.Item shouldGrow shouldShrink>
          <Text weight="bold">{term.name}</Text>
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("insightsTerms.editTerm")}
            renderIcon={IconEditLine}
            onClick={() => setTermModal(true)}
            size="small"
          />
        </Flex.Item>
        <Flex.Item>
          <IconButton
            screenReaderLabel={t("insightsTerms.deleteTerm")}
            renderIcon={IconTrashLine}
            onClick={() => setDeleteModal(true)}
            size="small"
          />
        </Flex.Item>
      </Flex>
      <Flex wrap="wrap">
        <Flex.Item size="100%">
          {t("termManagement.dateRange")}:{" "}
          <Pill>{`${new Date(term.start_at).toLocaleDateString()} - ${new Date(
            term.end_at
          ).toLocaleDateString()}`}</Pill>
        </Flex.Item>
        <Flex.Item size="100%">
          <Text lineHeight="double">
            {t("insightsTerms.associatedTerms")}:{" "}
            {!term.canvas_terms || term.canvas_terms.length === 0
              ? "None"
              : term.canvas_terms
                  .sort((a, b) => new Date(a.start_at) - new Date(b.start_at))
                  .map((t) => (
                    <Pill key={t.id} color="info" margin="0 small 0 0">
                      {t.name}
                    </Pill>
                  ))}
          </Text>
        </Flex.Item>
      </Flex>
      <DialogYesNo
        message={t("insightsTerms.deleteConfirm")}
        openStatus={deleteModal}
        onClickNo={() => setDeleteModal(false)}
        onClickYes={handleDeleteTerm}
      />
      <TermModal
        open={termModal}
        setOpen={setTermModal}
        term={term}
        handleSave={handleUpdateTerm}
      />
    </Card>
  );
};

export default TermCard;
